import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { AlertTriangle, Sparkles } from "lucide-react";
import BookDemo from "./BookDemo";

const PainToOutcomes = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const items = [
    {
      pain: {
        number: "4-8",
        unit: "weeks",
        text: "spent on collecting and uploading screenshots and evidences on compliance platforms",
      },
      solution: {
        number: "Always-on",
        unit: "AI agents",
        text: "Always working AI agents that find, validate, and file your compliance evidence automatically",
      },
    },
    {
      pain: {
        number: "Unguided",
        unit: "tools",
        text: "tools automates tasks but fails to help teams interpret or act",
      },
      solution: {
        number: "Expert",
        unit: "guidance",
        text: "Stuck on a control? Chat instantly with our compliance experts real people, real guidance.",
      },
    },
    {
      pain: {
        number: "Disconnected",
        unit: "audits",
        text: "Tools and auditors speak different languages leaving clients to bridge the gap every audit cycle.",
      },
      solution: {
        number: "Go-To",
        unit: "Hub",
        text: "From readiness to certification – Regodit handles all the audit back and forth, so you can focus on your business",
      },
    },
  ];

  return (
    <section
      id="why-regodit"
      ref={sectionRef}
      className="relative py-8 sm:py-16 md:py-24 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #0F0F2B 0%, #1A1A3A 50%, #0F0F2B 100%)",
      }}
    >
      <div className="relative max-w-7xl mx-auto px-6">
        {/* Main Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-8 md:mb-16"
        >
          <motion.h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 md:mb-6"
            initial={{ opacity: 0, y: 15 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            Others vs
            <span className="bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent">
              {" "}
              Regodit
            </span>
          </motion.h2>

          <motion.p
            className="text-sm sm:text-base lg:text-xl text-white/70 max-w-3xl mx-auto px-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
          >
            Experience what sets us apart: automated workflows, systematic
            evidence verification, continuous control mapping, and scalable
            compliance architecture.
          </motion.p>
        </motion.div>

        {/* Split Screen Comparisons */}
        <div className="space-y-6 sm:space-y-8 mb-16 sm:mb-20">
          {items.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10"
            >
              {/* LEFT - PAIN (Red) */}
              <div className="relative bg-gradient-to-br from-red-950/40 to-red-900/20 backdrop-blur-sm p-6 sm:p-8 md:p-10">
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-transparent pointer-events-none" />

                <div className="relative">
                  {/* Icon */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    </div>
                    <span className="text-red-400/60 text-sm font-semibold uppercase tracking-wider">
                      Others
                    </span>
                  </div>

                  {/* Number */}
                  <div className="mb-4">
                    <div className="text-5xl sm:text-6xl md:text-7xl font-black bg-gradient-to-br from-red-400 to-orange-500 bg-clip-text text-transparent leading-tight mb-2 pb-1 sm:pb-2 md:pb-3">
                      {item.pain.number}
                    </div>
                    <div className="text-red-400/70 text-sm sm:text-base font-medium uppercase tracking-wide">
                      {item.pain.unit}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-lg sm:text-xl text-white/70 leading-relaxed">
                    {item.pain.text}
                  </p>
                </div>
              </div>

              {/* RIGHT - SOLUTION (Blue) */}
              <div className="relative bg-gradient-to-br from-blue-950/40 to-cyan-900/20 backdrop-blur-sm p-6 sm:p-8 md:p-10 border-t lg:border-t-0 lg:border-l border-white/10">
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent pointer-events-none" />

                <div className="relative">
                  {/* Icon */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-blue-400" />
                    </div>
                    <span className="text-blue-400/60 text-sm font-semibold uppercase tracking-wider">
                      Regodit
                    </span>
                  </div>

                  {/* Number */}
                  <div className="mb-4">
                    <div className="text-5xl sm:text-6xl md:text-7xl font-black bg-gradient-to-br from-blue-400 to-cyan-400 bg-clip-text text-transparent leading-tight mb-2 pb-1 sm:pb-2 md:pb-3">
                      {item.solution.number}
                    </div>
                    <div className="text-blue-400/70 text-sm sm:text-base font-medium uppercase tracking-wide">
                      {item.solution.unit}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-lg sm:text-xl text-white/70 leading-relaxed">
                    {item.solution.text}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center"
        >
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to see the difference?
          </h3>
          <p className="text-white/60 mb-8 text-lg">
            Join companies who chose automation, expertise, and flexibility
          </p>
          <BookDemo
            variant="default"
            className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-4 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default PainToOutcomes;
